import React, {ChangeEvent, useCallback, useState} from 'react';
import { useTranslation } from 'react-i18next';
import Page, { IButton } from './Page';
import { DivAsLink, InputContainer, InputStyled, PageConnectFooterControl, PageConnectFooterText, StatusMessage } from '../../styledComponents';
import { InnerContainer } from '../../styledComponents/pages/start';
import { LoadingModal } from '../modals';
import { authUser, verifySmsCode } from '../../actions/actions';

interface IProps {
	shadow: boolean;
	onBack: () => void;
	number: string;
	queryString: string;
	authRequestId: string;
	test?:boolean;
}

function AuthScreen({ onBack, number, authRequestId, shadow, test, queryString }: IProps) {
	const { t } = useTranslation();
	const [error, setError] = useState('');
	const [smsCode, setSmsCode] = useState('');
	const [resend, setResend] = useState(false);
	const [resendLoading, setResendLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [tooManyAttemps, setTooManyAttemps] = useState(false);

	const handleCodeApply = useCallback(
			async (e?:React.KeyboardEvent<HTMLInputElement>) => {
				if(smsCode.length===6 && error.length===0 && (!e || e?.key === 'Enter')){
					setLoading(true);
					const response = await verifySmsCode({authRequestId, smsCode, test});
					if (response?.data?.access_token) {
						window.open(`https://consent.basiq.io/home${queryString}&token=${response?.data?.access_token}`, "_self");
					} else if (response?.statusCode === 204) {
						setLoading(false);
						setTooManyAttemps(true);
						setError(t('too_many_attempts'));
					  } else if (response?.error) {
						setLoading(false);
						setError(t('this_code_doesnt_appear_to_be_valid'));
					  }

				}
			},
		[smsCode, error, authRequestId, queryString, test]
	);
	const buttons: IButton[] = [
		{
			label: t('continue'),
			onClick: () => handleCodeApply(),
			disabled: smsCode.length<6 || !!error
		}
	];

	const handleResend = async () => {
		setError('');
		setSmsCode('');
		setResend(false);
		setResendLoading(true);
		const response = await authUser({authRequestId, test});
		setResendLoading(false);
		if (response.error) {
			setError(t('failed_to_send_sms'));
		} else {
			setResend(true);
		}
	};
	const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
		setError('');
		setResend(false);
		if(/^([0-9]+)$/.test(e.target.value) || e.target.value===''){
			setSmsCode(e.target.value);
		}
	};
	return (
		<Page
			title={t('verify_your_mobile_number')}
			subTitle={t('please_enter_the_sms_code_sent_to_your_mobile_number', { number })}
			shadow={shadow}
			buttons={buttons}
			onBack={onBack}
			backDisabled={tooManyAttemps}
		>
			<InnerContainer>
				<InputContainer>
					<InputStyled
						pattern="\d{6}"
						inputMode="numeric"
						autoComplete="one-time-code"
						type="text"
						maxLength={6}
						onChange={handleInput}
						value={smsCode}
						filled={smsCode.length === 6}
						error={tooManyAttemps || !!error}
                       	disabled={tooManyAttemps}
						onKeyDown={handleCodeApply}
					/>
				</InputContainer>
				<PageConnectFooterControl>
					{resend && <StatusMessage>{t('new_sms_code_has_been_sent')}</StatusMessage>}
					{!!error && <StatusMessage error>{error}</StatusMessage>}
					<PageConnectFooterText>
						<span>
							<>
							{t('didnt_receive_the_code')} <DivAsLink disabled={tooManyAttemps || resendLoading} onClick={() => !tooManyAttemps && !resendLoading && handleResend()}>{t('resend_sms')}</DivAsLink>
							</>
						</span>
					</PageConnectFooterText>
				</PageConnectFooterControl>
				<LoadingModal isOpen={loading} />
			</InnerContainer>
		</Page>
	);
}

export default AuthScreen;
